import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    serviceType: '',
    projectDetails: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('serviceType', formData.serviceType);
      formBody.append('projectDetails', formData.projectDetails);

      const response = await fetch('https://readdy.ai/api/form/d60fmogiku00ff9vl510', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          serviceType: '',
          projectDetails: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl">
          {/* Left Column - Contact Form */}
          <div className="bg-brand p-8 lg:p-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Get Your Free Estimate
            </h2>
            <p className="text-white/90 mb-10">
              Tell us about your project and we'll get back to you within 24 hours
            </p>

            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <div className="relative">
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b-2 border-white/40 text-white placeholder-white/60 py-3 focus:outline-none focus:border-white transition-colors"
                  placeholder="Full Name"
                />
              </div>

              {/* Email */}
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b-2 border-white/40 text-white placeholder-white/60 py-3 focus:outline-none focus:border-white transition-colors"
                  placeholder="Email Address"
                />
              </div>

              {/* Phone */}
              <div className="relative">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b-2 border-white/40 text-white placeholder-white/60 py-3 focus:outline-none focus:border-white transition-colors"
                  placeholder="Phone Number"
                />
              </div>

              {/* Service Type */}
              <div className="relative">
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b-2 border-white/40 text-white py-3 focus:outline-none focus:border-white transition-colors cursor-pointer"
                >
                  <option value="" disabled className="text-gray-900">Select Service Type</option>
                  <option value="Framing" className="text-gray-900">Framing</option>
                  <option value="Siding" className="text-gray-900">Siding</option>
                  <option value="Roofing" className="text-gray-900">Roofing</option>
                  <option value="Decking" className="text-gray-900">Decking</option>
                  <option value="Windows & Doors" className="text-gray-900">Windows & Doors</option>
                  <option value="General Construction" className="text-gray-900">General Construction</option>
                </select>
              </div>

              {/* Project Details */}
              <div className="relative">
                <textarea
                  id="projectDetails"
                  name="projectDetails"
                  value={formData.projectDetails}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full bg-transparent border-b-2 border-white/40 text-white placeholder-white/60 py-3 focus:outline-none focus:border-white transition-colors resize-none"
                  placeholder="Tell us about your project (max 500 characters)"
                />
                <div className="text-white/60 text-xs mt-1">
                  {formData.projectDetails.length}/500 characters
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-white text-brand py-4 rounded-xl font-bold hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Request Free Quote'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-white/20 text-white p-4 rounded-lg text-center">
                  <i className="ri-checkbox-circle-line text-2xl mr-2"></i>
                  Thank you! We'll contact you within 24 hours.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="bg-red-500/20 text-white p-4 rounded-lg text-center">
                  <i className="ri-error-warning-line text-2xl mr-2"></i>
                  Something went wrong. Please try again.
                </div>
              )}
            </form>
          </div>

          {/* Right Column - Contact Information */}
          <div className="bg-gray-900 p-8 lg:p-16 text-white">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4 lg:mb-6">Let's Build Together</h2>
            <p className="text-gray-300 mb-8 lg:mb-10 leading-relaxed text-sm lg:text-base">
              We're committed to delivering quality construction services that exceed your expectations. Reach out today to discuss your project.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 lg:space-y-8">
              {/* Phone */}
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-phone-line text-xl text-brand"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Phone</div>
                  <div className="space-y-1">
                    <a href="tel:+18605905605" className="text-base lg:text-xl font-semibold hover:text-brand transition-colors block">
                      (860) 590-5605
                    </a>
                    <a href="tel:+18722044484" className="text-base lg:text-xl font-semibold hover:text-brand transition-colors block">
                      (872) 204-4484
                    </a>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-mail-line text-xl text-brand"></i>
                </div>
                <div className="min-w-0">
                  <div className="text-sm text-gray-400 mb-1">Email</div>
                  <a href="mailto:allphasesbuildersllc@gmail.com" className="text-sm lg:text-base font-semibold hover:text-brand transition-colors break-all">
                    allphasesbuildersllc@gmail.com
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start space-x-4">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-map-pin-line text-xl text-brand"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Office</div>
                  <div className="text-sm lg:text-base">
                    10 Fieldstone Commons<br />
                    Tolland
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="mt-8 lg:mt-12 pt-6 lg:pt-8 border-t border-gray-700">
              <h3 className="text-base lg:text-lg font-semibold mb-4">Hours of Operation</h3>
              <div className="space-y-2 text-gray-300 text-sm">
                <div className="flex justify-between gap-4">
                  <span className="whitespace-nowrap">Monday - Friday</span>
                  <span className="whitespace-nowrap">7:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="whitespace-nowrap">Saturday</span>
                  <span className="whitespace-nowrap">8:00 AM - 4:00 PM</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="whitespace-nowrap">Sunday</span>
                  <span className="whitespace-nowrap">Closed</span>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-8 lg:mt-10">
              <div className="flex space-x-4">
                <a
                  href="#"
                  className="w-10 h-10 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-brand hover:text-brand transition-all"
                  aria-label="Facebook"
                >
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-brand hover:text-brand transition-all"
                  aria-label="Instagram"
                >
                  <i className="ri-instagram-line text-lg"></i>
                </a>
                <a
                  href="#"
                  className="w-10 h-10 rounded-full border-2 border-white/30 flex items-center justify-center hover:border-brand hover:text-brand transition-all"
                  aria-label="LinkedIn"
                >
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
